import type { OnboardingSegment } from "../../../models/onboarding";

export interface MissionContext {
  hasTenant: boolean;
  nbCandidats: number;
  hasQuittanceAuto: boolean;
  hasFiles: boolean;
  hasBank: boolean;
  hasCustomization: boolean;
  hasAbonnement: boolean;
  firstTenantId?: number;
  firstPropertyId?: number;
  firstProfileId?: number;
}

export interface MissionDef {
  id: string;
  title: string;
  desc: string;
  doneDesc: string;
  time: string;
  cta: string;
  facultatif?: boolean;
  donePill?: string;
  /**
   * Détection réelle de complétion. Les missions sans détection possible
   * (préparation d'un EDL, génération d'un bail…) renvoient false et sont
   * marquées via completed_missions au clic sur le CTA (manualComplete).
   */
  isDone: (ctx: MissionContext) => boolean;
  getPath: (ctx: MissionContext) => string;
  manualComplete?: boolean;
  /** Déclenche la modale « aha » quittance une fois la mission détectée faite. */
  aha?: boolean;
}

// Avec un locataire, l'activation des quittances se fait dans ses paramètres ;
// sans locataire (segment « entrée prévue » avant création), au niveau du
// profil bailleur (onglet Automatisation).
function quittanceAutoPath(ctx: MissionContext) {
  if (ctx.firstTenantId) {
    return `/bailleur/tenants/${ctx.firstTenantId}?tab-tenant-show=parametres-banque`;
  }
  return ctx.firstProfileId
    ? `/bailleur/profiles/${ctx.firstProfileId}?tab-bailleur-show=automatisation`
    : "/bailleur/profiles";
}

const MISSION_EDL: MissionDef = {
  id: "preparer-edl",
  title: "Préparez votre état des lieux",
  desc: "Modèle pièce par pièce avec photos. Il sera repris lors de la signature avec le locataire.",
  doneDesc: "État des lieux prêt pour l'entrée du locataire.",
  time: "2 min",
  cta: "Préparer",
  isDone: () => false,
  manualComplete: true,
  getPath: (ctx) =>
    ctx.firstPropertyId
      ? `/bailleur/properties/${ctx.firstPropertyId}?tab-prop-show=edl`
      : "/bailleur/properties",
};

export const CHECKLIST_1_MISSIONS: Record<OnboardingSegment, MissionDef[]> = {
  loue: [
    {
      id: "ajouter-locataire",
      title: "Ajoutez votre locataire",
      desc: "Nom, email, date d'entrée : Qalimo prépare ensuite le bail et le suivi des loyers.",
      doneDesc: "Locataire ajouté. Le suivi des loyers est en place.",
      time: "3 min",
      cta: "Ajouter mon locataire",
      isDone: (ctx) => ctx.hasTenant,
      getPath: () => "/bailleur/tenants/add",
    },
    {
      id: "documents-location",
      title: "Ajoutez les documents de la location",
      desc: "Bail signé, état des lieux, attestation d'assurance : tout est archivé et réutilisable en un clic.",
      doneDesc: "Documents archivés. Ils sont réutilisés automatiquement.",
      time: "2 min",
      cta: "Ajouter mes documents",
      isDone: (ctx) => ctx.hasFiles,
      getPath: (ctx) =>
        ctx.firstTenantId
          ? `/bailleur/tenants/${ctx.firstTenantId}?tab-tenant-show=documents-courriers`
          : "/bailleur/tenants",
    },
    {
      id: "quittances-auto",
      title: "Activez vos quittances automatiques",
      desc: "Votre locataire reçoit sa quittance chaque mois, sans aucune action de votre part.",
      doneDesc: "Vos quittances partent automatiquement chaque mois.",
      time: "1 min",
      cta: "Activer mes quittances",
      facultatif: true,
      donePill: "Actif",
      aha: true,
      isDone: (ctx) => ctx.hasQuittanceAuto,
      getPath: quittanceAutoPath,
    },
  ],
  recherche: [
    {
      id: "inviter-candidats",
      title: "Invitez vos premiers candidats",
      desc: "Un lien unique : chaque candidat dépose son dossier complet en ligne, vous comparez en un écran.",
      doneDesc: "Les dossiers arrivent directement dans Candidats.",
      time: "1 min",
      cta: "Inviter un candidat",
      isDone: (ctx) => ctx.nbCandidats > 0,
      manualComplete: true,
      getPath: () => "/bailleur/candidats/add",
    },
    MISSION_EDL,
    {
      id: "documents-bail",
      title: "Préparez les documents joints au contrat de location",
      desc: "Diagnostics, DPE, règlement de copropriété : ils seront annexés automatiquement au bail.",
      doneDesc: "Documents prêts. Ils seront annexés automatiquement au bail.",
      time: "2 min",
      cta: "Préparer mes documents",
      isDone: (ctx) => ctx.hasFiles,
      getPath: (ctx) =>
        ctx.firstPropertyId
          ? `/bailleur/properties/${ctx.firstPropertyId}?tab-prop-show=documents`
          : "/bailleur/properties",
    },
  ],
  entree: [
    {
      id: "generer-bail",
      title: "Générez le bail de votre locataire",
      desc: "Pré-rempli avec votre profil et votre bien, signature électronique incluse.",
      doneDesc: "Bail généré, prêt pour la signature.",
      time: "2 min",
      cta: "Générer le bail",
      isDone: () => false,
      manualComplete: true,
      getPath: (ctx) =>
        ctx.firstTenantId
          ? `/bailleur/tenants/${ctx.firstTenantId}?tab-tenant-show=actions`
          : "/bailleur/tenants",
    },
    MISSION_EDL,
    {
      id: "quittances-auto",
      title: "Programmez les quittances automatiques",
      desc: "Elles démarreront toutes seules à la date d'entrée du locataire.",
      doneDesc:
        "Quittances programmées : elles démarreront à la date d'entrée.",
      time: "1 min",
      cta: "Programmer",
      donePill: "Actif",
      aha: true,
      isDone: (ctx) => ctx.hasQuittanceAuto,
      getPath: quittanceAutoPath,
    },
  ],
};

export function getChecklist2Missions(hasAbonnement: boolean): MissionDef[] {
  return [
    {
      id: "sync-bancaire",
      title: "Activez la synchronisation bancaire",
      desc: "Qalimo rapproche automatiquement les loyers reçus et détecte les retards. Lecture seule, désactivable à tout moment.",
      doneDesc: "Banque connectée. Les loyers sont rapprochés automatiquement.",
      time: "2 min",
      cta: "Connecter ma banque",
      facultatif: true,
      donePill: "Actif",
      isDone: (ctx) => ctx.hasBank,
      getPath: () => "/bailleur/finances",
    },
    {
      id: "personnalisation",
      title: "Personnalisez votre compte",
      desc: "Logo, signature et adresse email d'envoi : vos documents et messages sont à vos couleurs.",
      doneDesc: "Compte personnalisé. Vos envois sont à vos couleurs.",
      time: "2 min",
      cta: "Personnaliser",
      isDone: (ctx) => ctx.hasCustomization,
      getPath: () => "/mon-compte",
    },
    hasAbonnement
      ? {
          id: "decouvrir-fiscalite",
          title: "Découvrez le module fiscalité",
          desc: "Votre déclaration de revenus fonciers pré-remplie à partir de votre comptabilité.",
          doneDesc: "Module fiscalité découvert.",
          time: "2 min",
          cta: "Découvrir",
          isDone: () => false,
          manualComplete: true,
          getPath: () => "/bailleur/fiscalite",
        }
      : {
          id: "choisir-offre",
          title: "Choisissez l'offre qui vous correspond",
          desc: "Continuez avec la formule adaptée à votre nombre de biens, sans engagement.",
          doneDesc: "Abonnement actif. Merci de votre confiance !",
          time: "1 min",
          cta: "Voir les offres",
          // Une fois l'abonnement pris, cette mission cède la place à
          // « découvrir la fiscalité » : tant qu'elle est affichée, elle est
          // par construction non faite.
          isDone: () => false,
          getPath: () => "/mon-compte",
        },
  ];
}
